import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import { FolderTree, Plus, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { CategoryTree } from "@/components/admin/products/CategoryTree";
import { CategoryForm } from "@/components/admin/products/CategoryForm";
import {
  useCategoryTree,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useReorderCategories,
} from "@/hooks/admin/useCategories";
import type { Category, CategoryFormData, ReorderItem } from "@/lib/admin-api/categories";

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  readonly open: boolean;
  readonly category: Category | null;
  readonly allCategories: readonly Category[];
  readonly onClose: () => void;
  readonly onConfirm: (moveChildrenTo: string | null) => void;
  readonly isDeleting: boolean;
}

function DeleteConfirmModal({
  open,
  category,
  allCategories,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteConfirmProps) {
  const { t } = useTranslation("admin");
  const [moveTarget, setMoveTarget] = useState<string>("");
  const [strategy, setStrategy] = useState<"move" | "delete_all">("delete_all");

  if (!category) return null;

  const hasChildren = category.children.length > 0;
  const hasProducts = category.product_count > 0;

  // Flatten categories for move target select, excluding the category being deleted and its descendants
  const flattenExcluding = (
    cats: readonly Category[],
    excludeId: string,
    depth = 0,
  ): { id: string; label: string }[] => {
    const result: { id: string; label: string }[] = [];
    for (const cat of cats) {
      if (cat.id === excludeId) continue;
      const prefix = "\u00A0\u00A0".repeat(depth);
      result.push({ id: cat.id, label: `${prefix}${cat.name_fr}` });
      if (cat.children.length > 0) {
        result.push(...flattenExcluding(cat.children, excludeId, depth + 1));
      }
    }
    return result;
  };

  const moveOptions = flattenExcluding(allCategories, category.id);

  const handleConfirm = () => {
    if (hasChildren && strategy === "move" && moveTarget) {
      onConfirm(moveTarget);
    } else {
      onConfirm(null);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onClose} disabled={isDeleting}>
        {t("actions.cancel")}
      </Button>
      <Button
        variant="destructive"
        onClick={handleConfirm}
        disabled={isDeleting || (hasChildren && strategy === "move" && !moveTarget)}
      >
        {isDeleting ? t("categories.deleting") : t("actions.delete")}
      </Button>
    </>
  );

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={t("categories.delete_title")}
      footer={footer}
      size="md"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">
              {t("categories.delete_confirm", { name: category.name_fr })}
            </p>
            {hasProducts && (
              <p className="mt-1">
                {t("categories.delete_has_products", { count: category.product_count })}
              </p>
            )}
          </div>
        </div>

        {hasChildren && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-stone-700">
              {t("categories.delete_has_children", { count: category.children.length })}
            </p>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="delete_strategy"
                  value="move"
                  checked={strategy === "move"}
                  onChange={() => setStrategy("move")}
                  className="text-amber-600 focus:ring-amber-500"
                />
                {t("categories.move_children_to")}
              </label>

              {strategy === "move" && (
                <select
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                  className="ml-6 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">{t("categories.select_parent")}</option>
                  {moveOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="delete_strategy"
                  value="delete_all"
                  checked={strategy === "delete_all"}
                  onChange={() => setStrategy("delete_all")}
                  className="text-red-600 focus:ring-red-500"
                />
                {t("categories.delete_all_children")}
              </label>
            </div>
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorState({ message }: { readonly message: string }) {
  const { t } = useTranslation("admin");
  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <AlertTriangle className="h-10 w-10 text-red-400" />
      <p className="mt-3 text-sm font-medium text-stone-600">
        {t("categories.error_loading")}
      </p>
      <p className="mt-1 text-xs text-stone-400">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Categories() {
  const { t } = useTranslation("admin");

  // Data fetching
  const { data: categories, isLoading, error } = useCategoryTree();

  // Mutations
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const reorderMutation = useReorderCategories();

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  // Handlers
  const handleCreate = useCallback(() => {
    setEditCategory(null);
    setParentIdForNew(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((cat: Category) => {
    setEditCategory(cat);
    setParentIdForNew(null);
    setFormOpen(true);
  }, []);

  const handleAddChild = useCallback((parent: Category) => {
    setEditCategory(null);
    setParentIdForNew(parent.id);
    setFormOpen(true);
  }, []);

  const handleFormSubmit = useCallback(
    async (data: CategoryFormData) => {
      if (editCategory) {
        await updateMutation.mutateAsync({ id: editCategory.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    },
    [editCategory, updateMutation, createMutation],
  );

  const handleDelete = useCallback((cat: Category) => {
    setDeleteTarget(cat);
  }, []);

  const handleDeleteConfirm = useCallback(
    async (moveChildrenTo: string | null) => {
      if (!deleteTarget) return;
      await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        moveChildrenTo,
      });
      setDeleteTarget(null);
    },
    [deleteTarget, deleteMutation],
  );

  const handleReorder = useCallback(
    (items: readonly ReorderItem[]) => {
      reorderMutation.mutate(items);
    },
    [reorderMutation],
  );

  const allCategories = categories ?? [];

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-semibold text-stone-900">
            {t("categories.page_title")}
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            {t("categories.page_description")}
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t("categories.add")}
        </Button>
      </div>

      {/* Content */}
      {error ? (
        <ErrorState message={(error as Error).message} />
      ) : allCategories.length === 0 && !isLoading ? (
        <EmptyState
          icon={<FolderTree className="h-6 w-6" />}
          title={t("categories.empty")}
          description={t("categories.empty_hint")}
          action={{ label: t("categories.add"), onClick: handleCreate }}
        />
      ) : (
        <CategoryTree
          categories={allCategories}
          isLoading={isLoading}
          onEdit={handleEdit}
          onAddChild={handleAddChild}
          onDelete={handleDelete}
          onReorder={handleReorder}
        />
      )}

      {/* Create/Edit modal */}
      <CategoryForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        category={editCategory}
        parentId={parentIdForNew}
        allCategories={allCategories}
      />

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        category={deleteTarget}
        allCategories={allCategories}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </AdminLayout>
  );
}
